/**
 * Reply video modal controller for YouTube triggers.
 *
 * Usage:
 * - Any element with `data-video-trigger` plus one of:
 *   - `data-video-url="https://www.youtube.com/watch?v=ID"` (or embed URL)
 *   - `data-video-id="ID"` and `data-video-type="youtube"`
 *   - `data-video-playlist="PLAYLIST_ID"` (optional, YouTube only)
 *
 * Behavior:
 * - Click opens modal and injects iframe.
 * - Click flow requests unmuted playback via YouTube Player API.
 * - Close button, backdrop click, and `dialog:close` unload iframe.
 */
(function () {
  const SELECTORS = {
    modal: '#video-modal',
    frame: '#ni-video-modal-iframe',
    close: '[data-video-modal-close]',
    trigger: '[data-video-trigger], a[href*="youtube.com/embed/"]',
    awpPlayingContainer: '.awp1v-player-embed.awp1v-player--playing',
    awpPauseButton: 'button.awp1v-player--pause'
  };

  const YOUTUBE_HOST_REGEX = /youtube\.com|youtu\.be/;

  class ReplyVideoModal {
    /**
     * Stop active AWPlayer v1 audio before video playback starts.
     */
    #pauseActiveAudio() {
      const awpV1PlayingContainer = document.querySelector(SELECTORS.awpPlayingContainer);
      if (!(awpV1PlayingContainer instanceof HTMLElement)) return;

      const pauseButton = awpV1PlayingContainer.querySelector(SELECTORS.awpPauseButton);
      if (pauseButton instanceof HTMLButtonElement) {
        pauseButton.click();
      }
    }

    /**
     * Bind all listeners for modal open/close lifecycle.
     */
    init() {
      this.#attachDelegatedClicks();
      this.#attachCloseHandlers();
      window.addEventListener('DOMContentLoaded', () => this.#initFromUrl());
    }

    /**
     * Build a YouTube embed URL for modal playback.
     * @param {{ id: string, playlist?: string, autoplay?: number, mute?: number }} param0
     * @returns {string}
     */
    #ytEmbedUrl({ id, playlist, autoplay = 1, mute = 1 }) {
      const params = new URLSearchParams({
        autoplay: String(autoplay),
        mute: String(mute),
        rel: '0',
        enablejsapi: '1'
      });

      if (playlist) {
        params.set('playlist', playlist || id);
        params.set('loop', '1');
      }

      return `https://www.youtube.com/embed/${id}?${params.toString()}`;
    }

    /**
     * Parse video payload from trigger dataset/href.
     * @param {Element} target
     * @returns {{ type: string, id: string, playlist?: string } | null}
     */
    #parseVideoFromDataset(target) {
      const element = /** @type {HTMLElement} */ (target);
      let url = element.dataset.videoUrl;
      let type = element.dataset.videoType;
      let id = element.dataset.videoId;
      let playlist = element.dataset.videoPlaylist;

      // RTE links may not provide data attributes; use href as source URL.
      if (!url && target.tagName === 'A' && /** @type {HTMLAnchorElement} */ (target).href) {
        url = /** @type {HTMLAnchorElement} */ (target).href;
      }

      if (!id && url) {
        try {
          const parsedUrl = new URL(url, window.location.origin);
          if (YOUTUBE_HOST_REGEX.test(parsedUrl.hostname)) {
            type = 'youtube';
            const queryVideoId = parsedUrl.searchParams.get('v');
            if (queryVideoId) id = queryVideoId;
            if (!id && parsedUrl.pathname.includes('/embed/')) {
              const parts = parsedUrl.pathname.split('/');
              const embedIndex = parts.indexOf('embed');
              const embedId = parts[embedIndex + 1];
              if (embedIndex !== -1 && embedId) {
                id = embedId.split('?')[0];
              }
            }
            if (!id && parsedUrl.hostname === 'youtu.be') id = parsedUrl.pathname.slice(1);
            playlist ||= parsedUrl.searchParams.get('list') || undefined;
          }
        } catch (_error) {
          // Ignore invalid trigger URLs.
        }
      }

      if (!type || !id) return null;
      return { type, id, playlist };
    }

    /**
     * Create iframe and attach unmute command for click flow.
     * @param {{ type: string, id: string, playlist?: string, mute?: number }} video
     * @returns {HTMLIFrameElement}
     */
    #buildIframe({ type, id, playlist, mute }) {
      let src = '';
      if (type === 'youtube') src = this.#ytEmbedUrl({ id, playlist, mute });

      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.title = 'Video player';

      if (type === 'youtube' && mute === 0) {
        const sendUnmute = () => {
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'unMute', args: [] }),
            '*'
          );
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
            '*'
          );
        };

        iframe.addEventListener('load', () => {
          sendUnmute();
        });
      }

      return iframe;
    }

    /**
     * Open modal and inject iframe.
     * @param {{ type: string, id: string, playlist?: string, mute?: number }} video
     */
    #openVideo({ type, id, playlist, mute = 1 }) {
      // Non-gesture opens (e.g. URL params) should stay muted by default.
      // Modal markup is rendered globally via `reply-video-modal` in `layout/theme.liquid`.
      const modal = document.querySelector(SELECTORS.modal);
      const frame = document.querySelector(SELECTORS.frame);
      if (!modal || !frame) {
        // eslint-disable-next-line no-console
        console.warn('ReplyVideoModal: Modal markup missing. Check `reply-video-modal` render in layout/theme.liquid.');
        return;
      }

      this.#pauseActiveAudio();

      frame.innerHTML = '';
      frame.appendChild(this.#buildIframe({ type, id, playlist, mute }));
      if ('showDialog' in modal && typeof modal.showDialog === 'function') {
        modal.showDialog();
      }
    }

    /**
     * Close modal and unload iframe.
     */
    #closeVideo() {
      const modal = document.querySelector(SELECTORS.modal);
      const frame = document.querySelector(SELECTORS.frame);
      if (!modal || !frame) return;

      frame.innerHTML = '';
      if ('closeDialog' in modal && typeof modal.closeDialog === 'function') {
        modal.closeDialog();
      }
    }

    /**
     * Listen for clicks on all video triggers.
     */
    #attachDelegatedClicks() {
      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const trigger = event.target.closest(SELECTORS.trigger);
        if (!trigger) return;

        event.preventDefault();
        const info = this.#parseVideoFromDataset(trigger);
        // Click is an explicit user gesture, so request unmuted playback.
        if (info) this.#openVideo({ ...info, mute: 0 });
      });
    }

    /**
     * Bind close interactions (button, backdrop, custom close event).
     */
    #attachCloseHandlers() {
      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        if (event.target.closest(SELECTORS.close)) {
          event.preventDefault();
          this.#closeVideo();
        }
      });

      document.addEventListener('click', (event) => {
        const modal = document.querySelector(SELECTORS.modal);
        if (!modal) return;
        const dialog = modal.querySelector('dialog');
        // Clicking dialog backdrop closes and unloads the player.
        if (event.target === dialog) this.#closeVideo();
      });

      document.addEventListener('dialog:close', (event) => {
        const modal = document.querySelector(SELECTORS.modal);
        if (event.target !== modal) return;

        // Ensure iframe is cleared for any external dialog close trigger.
        const frame = document.querySelector(SELECTORS.frame);
        if (frame) frame.innerHTML = '';
      });
    }

    /**
     * Optionally open a video from query/hash parameters.
     */
    #initFromUrl() {
      const url = new URL(window.location.href);
      const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
      const search = url.searchParams;
      /**
       * @param {string} key
       * @returns {string | null}
       */
      const get = (key) => search.get(key) || hash.get(key);

      const videoId = get('video');
      const videoType = get('type');
      const list = get('list') || get('playlist');

      if (videoId && videoType) {
        this.#openVideo({ type: videoType, id: videoId, playlist: list || undefined });
      } else if (videoId && /^[A-Za-z0-9_-]{6,}$/.test(videoId)) {
        // Fallback: interpret plain video param as YouTube ID.
        this.#openVideo({ type: 'youtube', id: videoId, playlist: list || undefined });
      }
    }
  }

  new ReplyVideoModal().init();
})();
